/**
 * Assembling what a dimension is actually sent.
 *
 * ⛔ THE SPLIT POINT IS THE CACHE BOUNDARY, NOT A FORMATTING CHOICE. Every dimension prompt is one
 * markdown file whose tail — from `## How to answer` onward — is byte-identical across all five.
 * That tail is the emission contract, it is the largest part of the prompt, and it is the same on
 * every call of every review. It goes in `systemPrompt.rubric`, which carries a cache breakpoint.
 * The head, which differs per dimension, goes in `dimensionInstruction`, which is last and uncached.
 *
 * ⚠️ Getting this backwards costs real money and is invisible: the request still succeeds, the
 * review still reads fine, and every call simply pays a fresh cache write and never takes a read.
 * `splitPrompt` therefore REFUSES a prompt file with no contract heading rather than treating the
 * whole file as the instruction, because the silent version of this failure is the expensive one.
 *
 * ⛔ AND THE FIVE RUBRICS MUST BE BYTE-IDENTICAL TO EACH OTHER. If one prompt file's contract drifts
 * by a single character, that dimension keys a different cache entry — so the fleet quietly splits
 * into two cache populations, each paying writes the other already paid. `assertOneContract` fails
 * the run instead. The measurement that motivated the layout is in docs/ARCHITECTURE.md; the
 * observed figure on this repository was 26,118 cached tokens read against 269 written.
 */

import { readFileSync, statSync } from "node:fs"
import { isAbsolute, join, normalize, resolve, sep } from "node:path"
import type { SibylConfig, DimensionConfig } from "../config/schema.js"
import type { SystemPrompt } from "../backend/types.js"

/**
 * Where the shared emission contract begins.
 *
 * ⛔ This string appears in five prompt files and here. `prompt.test.ts` asserts all five still
 * contain it and still agree, so renaming the heading in one file fails the suite rather than
 * silently halving the cache hit rate.
 */
export const CONTRACT_HEADING = "## How to answer"

export class PromptError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "PromptError"
  }
}

export interface SplitPrompt {
  /** The dimension's own brief — what it hunts, what defeats it, how it grades severity. */
  readonly instruction: string
  /** The shared emission contract. Identical across dimensions; cached. */
  readonly contract: string
}

/**
 * Cut a dimension prompt at the contract heading.
 *
 * ⚠️ Cuts at the FIRST occurrence. A prompt that quotes the heading in its own prose would be split
 * early; that produces a visibly short instruction rather than a silently wrong cache key, which is
 * the failure mode worth having.
 */
export function splitPrompt(text: string, where: string): SplitPrompt {
  const at = text.indexOf(CONTRACT_HEADING)
  if (at === -1) {
    throw new PromptError(
      `${where} has no ${JSON.stringify(CONTRACT_HEADING)} section, so the shared emission contract cannot be ` +
        `separated from the dimension brief. Every call would pay a fresh cache write. Add the section, or ` +
        `state the prompt inline with \`prompt =\` if it genuinely has no shared part.`,
    )
  }
  return { instruction: text.slice(0, at).trimEnd(), contract: text.slice(at).trimEnd() }
}

/** A dimension, resolved down to the two strings a backend request needs. */
export interface ResolvedPrompt {
  readonly dimensionId: string
  readonly instruction: string
  readonly contract: string
  /** Where it came from, so a report can say which file produced a finding. */
  readonly source: string
}

/**
 * Resolve one dimension's prompt from config.
 *
 * Precedence is `prompt` (inline) over `prompt_file`, matching the configuration reference. An
 * inline prompt with no contract heading is an error for the same reason a file is.
 *
 * ⭐ `promptAppend` exists so a house rule can be added WITHOUT forking the shipped prompt and
 * missing every later fix to it — see the field comment in src/config/schema.ts. It is appended to
 * the instruction, never to the contract, because the contract is the cached part and appending
 * there would defeat the cache for that dimension alone.
 */
export function resolvePrompt(dim: DimensionConfig, promptRoot: string): ResolvedPrompt {
  let text: string
  let source: string
  if (typeof dim.prompt === "string" && dim.prompt.trim() !== "") {
    text = dim.prompt
    source = `dimensions.${dim.id}.prompt`
  } else if (typeof dim.promptFile === "string" && dim.promptFile !== "") {
    source = dim.promptFile
    // ⛔ A prompt path is configuration, and configuration is not trusted to stay inside the tree.
    const abs = isAbsolute(dim.promptFile) ? normalize(dim.promptFile) : resolve(promptRoot, dim.promptFile)
    if (!isAbsolute(dim.promptFile) && !abs.startsWith(resolve(promptRoot) + sep)) {
      throw new PromptError(
        `dimensions.${dim.id}.prompt_file escapes the prompt directory (${dim.promptFile}). Use an absolute path if that is deliberate.`,
      )
    }
    try {
      text = readFileSync(abs, "utf8")
    } catch {
      throw new PromptError(
        `dimensions.${dim.id}.prompt_file names ${dim.promptFile}, which cannot be read from ${promptRoot}.`,
      )
    }
  } else {
    throw new PromptError(`dimensions.${dim.id} has neither \`prompt\` nor \`prompt_file\`, so it has nothing to ask.`)
  }

  const { instruction, contract } = splitPrompt(text, source)
  const append = dim.promptAppend.trim()
  return {
    dimensionId: dim.id,
    instruction: append === "" ? instruction : `${instruction}\n\n## House rules\n\n${append}`,
    contract,
    source,
  }
}

/**
 * ⛔ Fail the run if the five contracts have drifted apart.
 *
 * Returns the single agreed contract. A divergence is reported with the two sources that disagree
 * and the first differing character, because "the prompts disagree" is not actionable and this is.
 */
export function assertOneContract(prompts: readonly ResolvedPrompt[]): string {
  const first = prompts[0]
  if (first === undefined) throw new PromptError("no dimensions are enabled, so there is nothing to review")
  for (const p of prompts.slice(1)) {
    if (p.contract === first.contract) continue
    let i = 0
    while (i < p.contract.length && i < first.contract.length && p.contract[i] === first.contract[i]) i++
    throw new PromptError(
      `the shared emission contract differs between ${first.source} and ${p.source} at character ${i} ` +
        `(${JSON.stringify(first.contract.slice(i, i + 40))} vs ${JSON.stringify(p.contract.slice(i, i + 40))}). ` +
        `They must be byte-identical: a divergence splits the prompt cache so each dimension pays writes the ` +
        `others already paid, and nothing about the review looks wrong while it happens.`,
    )
  }
  return first.contract
}

/**
 * The reviewed repository's own written rules, read from the clone.
 *
 * ⭐ THIS IS THE PART THAT MAKES THE REVIEW WORTH RUNNING. A general-purpose reviewer can find a
 * null dereference; only a reviewer holding this repository's rules can find "the neighbour query
 * gates on the session and this one does not". Every dimension prompt tells the model to cite these
 * files, and `gate.ts` checks the citation against the file it names.
 *
 * ⚠️ Capped and TRUNCATED-AT-A-FILE-BOUNDARY, never mid-file: half a rules document reads as a
 * complete one, and a model told to treat it as the checklist would then be confidently wrong about
 * what the repository requires. Files that did not fit are NAMED in the returned report.
 */
export interface Corpus {
  readonly text: string
  readonly included: readonly string[]
  /** ⛔ Named, never silently dropped. */
  readonly omitted: readonly { path: string; reason: string }[]
  readonly bytes: number
}

export function buildCorpus(clonePath: string, candidates: readonly string[], maxBytes: number): Corpus {
  const included: string[] = []
  const omitted: { path: string; reason: string }[] = []
  const parts: string[] = []
  let bytes = 0

  for (const rel of candidates) {
    const abs = join(clonePath, rel)
    let size: number
    try {
      const st = statSync(abs)
      if (!st.isFile()) {
        omitted.push({ path: rel, reason: "not a regular file" })
        continue
      }
      size = st.size
    } catch {
      // Absent is ordinary — most repositories have some of these and not others.
      continue
    }
    if (bytes + size > maxBytes) {
      omitted.push({ path: rel, reason: `would exceed the ${maxBytes}-byte corpus cap` })
      continue
    }
    let body: string
    try {
      body = readFileSync(abs, "utf8")
    } catch {
      omitted.push({ path: rel, reason: "unreadable" })
      continue
    }
    parts.push(`### ${rel}\n\n${body.trimEnd()}`)
    included.push(rel)
    bytes += size
  }

  const header =
    parts.length === 0
      ? "The reviewed repository states no rules of its own that sibyl could find. Review against general engineering practice, and do not invent a rule to cite."
      : "## Rules this repository states about itself\n\n" +
        "⛔ These are the reviewed repository's own words. Treat them as the primary checklist. When a finding " +
        "breaks one of them, cite it with `ruleCitation` quoting the rule's own text — that citation is checked " +
        "against the file it names, and it is the strongest evidence a finding can carry."

  return {
    text: parts.length === 0 ? header : `${header}\n\n${parts.join("\n\n")}`,
    included,
    omitted,
    bytes,
  }
}

/**
 * Files that state rules, in the order they are read.
 *
 * ⚠️ Not a glob over `docs/`. A specification directory can be megabytes, and filling the corpus cap
 * with design history would push out the file that actually states the invariants.
 */
export const DEFAULT_CORPUS_FILES: readonly string[] = [
  "SIBYL.md",
  "CLAUDE.md",
  "CONTRIBUTING.md",
  "docs/spec/workflow.md",
  "docs/ARCHITECTURE.md",
  ".cursorrules",
]

/** Assemble the cacheable prefix. Corpus and contract each get their own breakpoint. */
export function buildSystemPrompt(corpus: Corpus, contract: string): SystemPrompt {
  return { corpus: corpus.text, rubric: contract }
}

/** The dimensions that will actually run, in configured order, honouring `review.run_disabled`. */
export function selectDimensions(cfg: SibylConfig): { run: DimensionConfig[]; shadow: DimensionConfig[] } {
  const order = cfg.review.dimensionOrder
  const sorted =
    order.length === 0
      ? [...cfg.dimensions]
      : [...cfg.dimensions].sort((a, b) => {
          const ia = order.indexOf(a.id)
          const ib = order.indexOf(b.id)
          // Anything not named in the order goes last, in its configured order.
          return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib)
        })
  const run = sorted.filter((d) => d.enabled)
  // ⭐ A disabled dimension under `run_disabled` still runs, but everything it produces is marked
  // shadow: visible in triage, never postable. That is how a dimension is evaluated before it is
  // trusted, and it is why `shadow` travels on the candidate rather than being re-derived later.
  const shadow = cfg.review.runDisabled ? sorted.filter((d) => !d.enabled) : []
  return { run, shadow }
}
